<template>
  <div class="p-6 max-w-5xl mx-auto space-y-10">

    <!-- Page header -->
    <header>
      <h1 class="text-3xl font-bold text-app-primary tracking-tight">Appearance</h1>
      <p class="text-app-secondary mt-1.5 text-sm">
        Choose how PulsarAI looks to you. Switch between dark and light mode, then pick a light theme variant that matches your taste.
      </p>
    </header>

    <!-- ============================================================
         Mode toggle (Dark / Light)
         ============================================================ -->
    <section class="space-y-3">
      <h2 class="text-sm font-semibold uppercase tracking-wider text-app-tertiary">Mode</h2>
      <div class="grid grid-cols-2 gap-4 max-w-md">
        <button
          v-for="mode in modeOptions"
          :key="mode.id"
          @click="setMode(mode.id)"
          class="glass-panel p-4 text-left transition-all duration-200 hover:scale-[1.02] cursor-pointer"
          :style="{
            boxShadow: themeMode === mode.id ? `0 0 0 2px var(--accent-solid), var(--panel-shadow)` : 'var(--panel-shadow)',
          }"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center"
              :style="{ background: mode.id === 'dark' ? '#0B0D17' : 'var(--app-bg)', border: '1px solid var(--divider)' }"
            >
              <Icon :name="mode.icon" class="w-5 h-5" :style="{ color: mode.id === 'dark' ? '#C4B5FD' : 'var(--accent-solid)' }" />
            </div>
            <div>
              <p class="font-semibold text-app-primary text-sm">{{ mode.label }}</p>
              <p class="text-xs text-app-tertiary">{{ mode.blurb }}</p>
            </div>
          </div>
        </button>
      </div>
    </section>

    <!-- ============================================================
         Light variant picker. Disabled-feeling (but still visible)
         when dark mode is active — picking one auto-switches into
         light mode so the change is immediately previewable.
         ============================================================ -->
    <section class="space-y-3">
      <div class="flex items-baseline justify-between">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-app-tertiary">Light Theme Variant</h2>
        <p v-if="themeMode === 'dark'" class="text-xs text-app-tertiary">
          Selecting one will switch you out of dark mode.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          v-for="v in variants"
          :key="v.id"
          @click="onPickVariant(v.id)"
          class="glass-panel p-0 overflow-hidden text-left transition-all duration-200 hover:scale-[1.01] cursor-pointer flex flex-col"
          :style="{
            boxShadow: lightVariant === v.id && themeMode === 'light'
              ? `0 0 0 2px var(--accent-solid), var(--panel-shadow)`
              : 'var(--panel-shadow)',
          }"
        >
          <!-- Live mini-mockup for this variant. Uses the same
               sidebar/header/panel surfaces the real app uses, so
               the preview is faithful (changes here = changes
               there). -->
          <ThemePreviewCard :variant="v.id" :is-active="lightVariant === v.id && themeMode === 'light'" />

          <div class="p-4 space-y-2 border-t border-app-divider">
            <div class="flex items-center justify-between gap-3">
              <h3 class="font-semibold text-app-primary text-base">{{ v.name }}</h3>
              <div v-if="lightVariant === v.id && themeMode === 'light'"
                   class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                   :style="{ background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-text)' }">
                Active
              </div>
              <div v-else-if="v.id === defaultVariant"
                   class="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full text-app-tertiary border border-app-divider">
                Default
              </div>
            </div>
            <p class="text-xs text-app-secondary leading-relaxed">{{ v.blurb }}</p>
            <div class="flex gap-1 pt-1">
              <span
                v-for="(s, i) in v.swatches"
                :key="i"
                class="w-5 h-5 rounded"
                :style="{ background: s, border: '1px solid var(--divider)' }"
              />
            </div>
          </div>
        </button>
      </div>
    </section>

    <!-- Footer note -->
    <p class="text-xs text-app-tertiary border-t border-app-divider pt-4">
      Your preference is saved in this browser. Sign in on a different device or browser to set it again there.
    </p>

  </div>
</template>

<script setup lang="ts">
// 💬 Read + write theme via the shared composable. The picker
// auto-switches into light mode when a variant is selected so the
// change is visible without an extra click on the dark/light toggle.
const { themeMode, lightVariant, variants, defaultVariant, setMode, setLightVariant } = useTheme()

const modeOptions = [
  { id: 'dark' as const, label: 'Dark', blurb: 'The original space / nebula theme.', icon: 'heroicons:moon' },
  { id: 'light' as const, label: 'Light', blurb: 'Bright and airy — choose a variant below.', icon: 'heroicons:sun' },
]

const onPickVariant = (id: typeof variants[number]['id']) => {
  setLightVariant(id)
  if (themeMode.value !== 'light') setMode('light')
}
</script>
